/**
 * Club billing details — the invoicing identity a club maintains in Settings → Billing (TW-3527).
 *
 * The printed legal identity (company name, tax number/CIF, billing address) is **versioned**:
 * each edit appends a new `active` row and supersedes the prior one, so an invoice can later link to
 * the exact billing version it was generated from and historical invoices never drift (see
 * docs/adr/0001). The **accounting email** is delivery metadata, NOT part of the printed factura, so
 * it lives unversioned on the `Club` record and editing it never spins a new version.
 *
 * As elsewhere, this Zod schema is the single source of truth for request validation (API + web).
 */
import { z } from "zod";

// ── Validation ──────────────────────────────────────────────────────────────

/** A short free-text address part — trimmed, length-capped, optional (a club may save without it). */
const optionalAddressPart = (max: number) => z.string().trim().max(max).optional();

/**
 * Spanish CIF, lenient: a tax number is letters/digits with optional dashes/spaces/dots (e.g.
 * `G-08476038`). We deliberately do NOT verify the CIF control character — a strict checksum is
 * brittle and rejects legitimate edge cases; the real validation is a human reading the factura.
 */
const taxIdSchema = z
  .string()
  .trim()
  .min(1, "Tax number is required")
  .max(20)
  .regex(/^[A-Za-z0-9][A-Za-z0-9.\s-]{4,18}[A-Za-z0-9]$/, "Enter a valid tax number (CIF)")
  .transform((v) => v.toUpperCase());

/**
 * Save the club's billing details. Company name, tax number and accounting email are required (they
 * appear on every factura / receive every invoice); the address parts are optional to save. Editing
 * the legal-identity fields appends a new billing version; the accounting email is updated in place.
 */
export const clubBillingSchema = z.object({
  companyName: z.string().trim().min(2, "Company name is required").max(120),
  taxId: taxIdSchema,
  street: optionalAddressPart(200),
  postalCode: optionalAddressPart(20),
  city: optionalAddressPart(120),
  province: optionalAddressPart(120),
  accountingEmail: z.string().trim().toLowerCase().email("Enter a valid email").max(200),
});
export type ClubBillingInput = z.infer<typeof clubBillingSchema>;

// ── DTO ───────────────────────────────────────────────────────────────────────

/**
 * The club's current billing details as returned to the client: the active legal-identity version's
 * fields (empty strings when a club has no billing set yet) plus the unversioned accounting email.
 * `versionId`/`updatedAt`/`updatedBy` describe the active version (all null when none exists yet) so
 * the UI can show when billing was last changed and by whom.
 */
export interface ClubBillingDto {
  companyName: string;
  taxId: string;
  street: string;
  postalCode: string;
  city: string;
  province: string;
  accountingEmail: string;
  versionId: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}
